/**
 * Record Patient Payment — right-side drawer over the Live Patient Snapshot.
 *
 * Follows the product design: transaction-kind selector, amount with quick
 * picks, payment type, method, then submit.
 *
 * SCOPE (what the Enter API actually supports today — see enter-payments.ts):
 *  - PAYMENT  ✅ POST /provider/collection/payment
 *  - DISCOUNT ❌ Enter exposes no create-adjustment endpoint
 *  - REFUND   ✅ POST /provider/collection/payment/{id}/refund — targets an
 *             EXISTING payment, so this sheet lists the patient's payments and
 *             caps the amount at what Enter still reports as refundable.
 *  - Card TERMINAL capture is out of scope by product decision.
 * The two unsupported modes render DISABLED with an explicit reason rather than
 * silently missing — a form that can't submit is worse than a labelled gap.
 */

import { useState } from 'react';
import { CreditCard, Loader2, Tag, Undo2, X } from 'lucide-react';
import type { PatientPaymentMethod, PatientPaymentType } from '@ctrl-enter/types';
import { PATIENT_PAYMENT_METHOD_LABEL } from '@ctrl-enter/types';
import { cn } from '../utils/cn';
import {
  usePaymentsQuery,
  useRecordPaymentMutation,
  useRefundPaymentMutation,
} from '../hooks/use-gateway-queries';

type TxKind = 'payment' | 'discount' | 'refund';

/**
 * Prefer the server's own explanation over a generic one. The gateway
 * distinguishes cases the user can act on ("only $40.00 can still be refunded",
 * "payments are not enabled in this environment") from ones they can't, and on
 * a money screen "please try again" is actively bad advice when retrying cannot
 * possibly work.
 */
function serverMessage(res: { body?: unknown }): string | null {
  const body = res.body;
  if (body && typeof body === 'object' && 'message' in body) {
    const message = (body as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) return message;
  }
  return null;
}

const TX_KINDS: Array<{
  key: TxKind;
  label: string;
  icon: typeof CreditCard;
  disabledReason?: string;
}> = [
  { key: 'payment', label: 'Payment', icon: CreditCard },
  {
    key: 'discount',
    label: 'Discount',
    icon: Tag,
    disabledReason: 'Discounts will be available once Enter supports posting adjustments.',
  },
  { key: 'refund', label: 'Refund', icon: Undo2 },
];

/** Payment types with their quick-pick amounts, per the design. */
const PAY_TYPES: Array<{
  key: PatientPaymentType;
  label: string;
  hint: string;
  quick: number[];
}> = [
  { key: 'copay', label: 'Copay', hint: 'Collected at time of visit', quick: [25, 35, 50] },
  {
    key: 'estimate',
    label: 'Estimate / prepay',
    hint: 'Patient responsibility estimate',
    quick: [75, 100, 150],
  },
  { key: 'noshow', label: 'No-show fee', hint: 'Missed-appointment charge', quick: [25, 50, 75] },
];

const METHODS: PatientPaymentMethod[] = ['card', 'cash', 'check'];

type RefundReason = 'overpay' | 'duplicate' | 'recoup';
const REFUND_REASONS: Array<{ key: RefundReason; label: string; hint: string }> = [
  { key: 'overpay', label: 'Overpayment', hint: 'Patient paid more than owed' },
  { key: 'duplicate', label: 'Duplicate payment', hint: 'Charged twice for one visit' },
  { key: 'recoup', label: 'Insurance recoupment', hint: 'Payer later covered patient portion' },
];

export function RecordPaymentSheet({
  open,
  onClose,
  accountId,
  claimId,
  patientName,
}: {
  open: boolean;
  onClose: () => void;
  /** Enter account that owns the claim — required to route the credential. */
  accountId: string;
  /** Claim to apply the payment to. Omitted → an unapplied patient payment. */
  claimId?: string;
  patientName?: string;
}) {
  const [kind, setKind] = useState<TxKind>('payment');
  const [type, setType] = useState<PatientPaymentType>('copay');
  const [method, setMethod] = useState<PatientPaymentMethod>('card');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [reason, setReason] = useState<RefundReason>('overpay');
  const [refundPaymentId, setRefundPaymentId] = useState<string | null>(null);
  const record = useRecordPaymentMutation();
  const refund = useRefundPaymentMutation();
  const paymentsQuery = usePaymentsQuery(accountId, claimId ? [claimId] : [], kind === 'refund');

  if (!open) return null;

  const activeType = PAY_TYPES.find(t => t.key === type) ?? (PAY_TYPES[0] as (typeof PAY_TYPES)[0]);
  const parsed = Number(amount);
  const kindHint = TX_KINDS.find(k => k.key === kind)?.disabledReason;
  const isRefund = kind === 'refund';
  const pending = record.isPending || refund.isPending;

  const payments =
    paymentsQuery.data?.status === 200 ? paymentsQuery.data.body.payments : [];
  const selectedPayment = payments.find(p => p.id === refundPaymentId) ?? null;
  // Never let the UI offer more than Enter says is still refundable — the
  // server re-checks too, but the user shouldn't be able to type an amount
  // that's guaranteed to fail.
  const overRefund = isRefund && !!selectedPayment && parsed > selectedPayment.refundableAmount;

  const canSubmit =
    Number.isFinite(parsed) &&
    parsed > 0 &&
    !pending &&
    !done &&
    (isRefund ? !!selectedPayment && !overRefund : kind === 'payment');

  const submitRefund = () => {
    if (!selectedPayment) return;
    setError(null);
    refund.mutate(
      {
        params: { id: selectedPayment.id },
        body: {
          accountId,
          paymentId: selectedPayment.id,
          amount: parsed,
          reason,
          ...(note.trim() ? { note: note.trim() } : {}),
        },
      },
      {
        onSuccess: (res: { status: number; body?: unknown }) => {
          if (res.status === 200) {
            setDone(true);
            setTimeout(onClose, 1200);
          } else {
            setError(serverMessage(res) ?? 'Could not refund the payment. Please try again.');
          }
        },
        onError: () => setError('Could not refund the payment. Please try again.'),
      }
    );
  };

  const submitPayment = () => {
    setError(null);
    record.mutate(
      {
        body: {
          accountId,
          ...(claimId ? { claimId } : {}),
          amount: parsed,
          type,
          method,
          ...(note.trim() ? { note: note.trim() } : {}),
        },
      },
      {
        onSuccess: (res: { status: number; body?: unknown }) => {
          if (res.status === 200) {
            setDone(true);
            // Let the confirmation register before the drawer disappears.
            setTimeout(onClose, 1200);
          } else {
            setError(serverMessage(res) ?? 'Could not record the payment. Please try again.');
          }
        },
        onError: () => setError('Could not record the payment. Please try again.'),
      }
    );
  };

  const submit = () => (isRefund ? submitRefund() : submitPayment());

  return (
    <div className="absolute inset-0 z-40 flex">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="flex-1 bg-foreground/40"
      />

      <div className="w-full max-w-[380px] flex-none h-full bg-card border-l border-border flex flex-col overflow-y-auto">
        <header className="flex items-center gap-2 px-4 pt-3 pb-2 border-b border-border/40">
          <span className="flex items-center justify-center w-7 h-7 rounded-md bg-primary/15 text-primary shrink-0">
            <CreditCard size={15} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-foreground">
              {isRefund ? 'Issue Refund' : 'Record Patient Payment'}
            </p>
            {patientName && (
              <p className="text-[11px] text-muted-foreground truncate">{patientName}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 rounded-md text-muted-foreground hover:bg-muted/40 hover:text-foreground transition-colors"
          >
            <X size={14} />
          </button>
        </header>

        <div className="px-4 py-3 flex flex-col gap-4">
          <div className="grid grid-cols-3 gap-1.5">
            {TX_KINDS.map(k => {
              const KindIcon = k.icon;
              const disabled = !!k.disabledReason;
              return (
                <button
                  key={k.key}
                  type="button"
                  disabled={disabled}
                  title={k.disabledReason ?? k.label}
                  onClick={() => setKind(k.key)}
                  className={cn(
                    'flex flex-col items-center gap-1 py-2 rounded-md border text-[11px] transition-colors',
                    kind === k.key && !disabled
                      ? 'border-primary/40 bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground hover:bg-muted/40',
                    disabled && 'opacity-40 cursor-not-allowed hover:bg-transparent'
                  )}
                >
                  <KindIcon size={14} />
                  {k.label}
                </button>
              );
            })}
          </div>
          {kindHint && <p className="text-[11px] text-muted-foreground -mt-2">{kindHint}</p>}

          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-medium text-muted-foreground" htmlFor="pay-amount">
              Amount
            </label>
            <div className="flex items-center gap-1.5">
              <span className="text-[15px] text-muted-foreground">$</span>
              <input
                id="pay-amount"
                inputMode="decimal"
                value={amount}
                onChange={e => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                placeholder="0.00"
                className={cn(
                  'flex-1 rounded-md border border-border bg-background px-2 py-1.5',
                  'text-[15px] tabular-nums text-foreground placeholder:text-muted-foreground/50',
                  'focus:outline-none focus:ring-1 focus:ring-primary/40'
                )}
              />
            </div>
            {overRefund && selectedPayment && (
              <p className="text-[10px] text-destructive">
                Only ${selectedPayment.refundableAmount.toFixed(2)} of this payment can still be
                refunded.
              </p>
            )}
            <div className={cn('flex gap-1.5', isRefund && 'hidden')}>
              {activeType.quick.map(q => (
                <button
                  key={q}
                  type="button"
                  onClick={() => setAmount(String(q))}
                  className="flex-1 text-[11px] py-1 rounded-md border border-border text-muted-foreground hover:bg-muted/40 transition-colors tabular-nums"
                >
                  ${q}
                </button>
              ))}
            </div>
          </div>

          {isRefund && (
            <div className="flex flex-col gap-1.5">
              <span className="text-[11px] font-medium text-muted-foreground">
                Refund which payment?
              </span>
              {paymentsQuery.isLoading && (
                <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1.5">
                  <Loader2 size={11} className="animate-spin" /> Loading payments…
                </p>
              )}
              {!paymentsQuery.isLoading && payments.length === 0 && (
                <p className="text-[11px] text-muted-foreground">
                  No payments found on this patient's claims to refund.
                </p>
              )}
              {payments.map(p => {
                const exhausted = p.refundableAmount <= 0;
                return (
                  <button
                    key={p.id}
                    type="button"
                    disabled={exhausted}
                    onClick={() => {
                      setRefundPaymentId(p.id);
                      // Default to the full refundable balance — the common case.
                      setAmount(String(p.refundableAmount));
                    }}
                    className={cn(
                      'flex items-center justify-between px-2.5 py-1.5 rounded-md border text-left transition-colors',
                      refundPaymentId === p.id
                        ? 'border-primary/40 bg-primary/10'
                        : 'border-border hover:bg-muted/40',
                      exhausted && 'opacity-40 cursor-not-allowed hover:bg-transparent'
                    )}
                  >
                    <span className="min-w-0">
                      <span className="block text-[12px] text-foreground truncate">
                        {p.memo || 'Payment'}
                      </span>
                      <span className="block text-[10px] text-muted-foreground">
                        {p.paymentDate ?? ''}
                        {p.refundedAmount > 0 && ` · $${p.refundedAmount.toFixed(2)} refunded`}
                      </span>
                    </span>
                    <span className="text-[11px] tabular-nums text-muted-foreground whitespace-nowrap ml-2">
                      ${p.refundableAmount.toFixed(2)} left
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {isRefund && (
            <div className="flex flex-col gap-1.5">
              <span className="text-[11px] font-medium text-muted-foreground">Reason</span>
              {REFUND_REASONS.map(r => (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => setReason(r.key)}
                  className={cn(
                    'flex flex-col items-start px-2.5 py-1.5 rounded-md border text-left transition-colors',
                    reason === r.key
                      ? 'border-primary/40 bg-primary/10'
                      : 'border-border hover:bg-muted/40'
                  )}
                >
                  <span
                    className={cn(
                      'text-[12px]',
                      reason === r.key ? 'text-primary font-medium' : 'text-foreground'
                    )}
                  >
                    {r.label}
                  </span>
                  <span className="text-[10px] text-muted-foreground">{r.hint}</span>
                </button>
              ))}
            </div>
          )}

          {!isRefund && (
          <div className="flex flex-col gap-1.5">
            <span className="text-[11px] font-medium text-muted-foreground">Payment type</span>
            {PAY_TYPES.map(t => (
              <button
                key={t.key}
                type="button"
                onClick={() => setType(t.key)}
                className={cn(
                  'flex flex-col items-start px-2.5 py-1.5 rounded-md border text-left transition-colors',
                  type === t.key
                    ? 'border-primary/40 bg-primary/10'
                    : 'border-border hover:bg-muted/40'
                )}
              >
                <span
                  className={cn(
                    'text-[12px]',
                    type === t.key ? 'text-primary font-medium' : 'text-foreground'
                  )}
                >
                  {t.label}
                </span>
                <span className="text-[10px] text-muted-foreground">{t.hint}</span>
              </button>
            ))}
          </div>
          )}

          {!isRefund && (
          <div className="flex flex-col gap-1.5">
            <span className="text-[11px] font-medium text-muted-foreground">Method</span>
            <div className="grid grid-cols-3 gap-1.5">
              {METHODS.map(m => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMethod(m)}
                  className={cn(
                    'py-1.5 rounded-md border text-[11px] transition-colors',
                    method === m
                      ? 'border-primary/40 bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground hover:bg-muted/40'
                  )}
                >
                  {PATIENT_PAYMENT_METHOD_LABEL[m]}
                </button>
              ))}
            </div>
            {method === 'card' && (
              <p className="text-[10px] text-muted-foreground/80">
                Records a card payment. Terminal capture isn't wired up yet.
              </p>
            )}
          </div>
          )}

          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Note (optional) — e.g. check #1042"
            rows={2}
            className={cn(
              'w-full resize-none rounded-md border border-border bg-background px-2 py-1',
              'text-[11px] text-foreground placeholder:text-muted-foreground/60',
              'focus:outline-none focus:ring-1 focus:ring-primary/40'
            )}
          />

          {error && <p className="text-[11px] text-destructive">{error}</p>}
          {done && <p className="text-[11px] text-emerald-600">Payment recorded ✓</p>}
        </div>

        <div className="mt-auto px-4 py-3 border-t border-border/40">
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className={cn(
              'w-full inline-flex items-center justify-center gap-1.5 text-[12px] py-2 rounded-md',
              'bg-primary text-primary-foreground hover:bg-primary/90 transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {pending && <Loader2 size={13} className="animate-spin" />}
            {pending
              ? isRefund
                ? 'Refunding…'
                : 'Recording…'
              : isRefund
                ? `Refund ${parsed > 0 ? `$${parsed.toFixed(2)}` : ''}`.trim()
                : `Record ${parsed > 0 ? `$${parsed.toFixed(2)}` : 'payment'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
